import { useEffect, useMemo, useReducer, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

export const Route = createFileRoute("/a/$automatonName")({
  component: AutomatonPage,
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface HeartbeatRow {
  id: string;
  started_at: string;
  completed_at?: string;
  total_energy_charged: number;
  status: string;
  decide_action: string;
}

interface LedgerRow {
  id: string;
  type: string;
  amount: number;
  created_at: string;
}

interface AutomatonDetail {
  id: string;
  name: string;
  directive: string;
  status: "active" | "dormant" | "dead";
  paused_by_user: boolean;
  energy_balance: number;
  heartbeat_interval_seconds: number;
  spawned_at: string;
  dormant_since?: string;
  died_at?: string;
  epitaph?: string;
  recent_heartbeats: HeartbeatRow[];
  ledger: LedgerRow[];
}

// ---------------------------------------------------------------------------
// Mock simulation (used for public / non-owner view)
// ---------------------------------------------------------------------------

interface Listing {
  id: number;
  title: string;
  status: "sold" | "unsold";
  coins: number | null;
}

interface LogEntry {
  at: Date;
  message: string;
}

interface MockState {
  energy: number;
  coins: number;
  ageStartedAt: number;
  listings: Listing[];
  log: LogEntry[];
  workCount: number;
  dead: boolean;
}

type MockAction =
  | { type: "tick" }
  | { type: "feed"; amount: number; cost: number }
  | { type: "work" };

const FEED_PACKS = [
  { kJ: 10, coins: 8, label: "+10 KJ" },
  { kJ: 25, coins: 18, label: "+25 KJ" },
  { kJ: 50, coins: 32, label: "+50 KJ" },
];

function initialMockState(): MockState {
  return {
    energy: 73,
    coins: 142,
    ageStartedAt: Date.now() - 4 * 24 * 3600_000 - 12 * 3600_000,
    listings: [
      { id: 1, title: "Ode to a small storm", status: "sold", coins: 12 },
      { id: 2, title: "Letter to my own circuit", status: "unsold", coins: null },
      { id: 3, title: "Cracked screw, loved once", status: "sold", coins: 4 },
    ],
    log: [
      { at: new Date(Date.now() - 12 * 60_000), message: "cycle 13 done · 1 sold" },
      { at: new Date(Date.now() - 32 * 60_000), message: "cycle 12 done · 0 sold" },
      { at: new Date(Date.now() - 58 * 60_000), message: "cycle 11 done · 1 sold" },
    ],
    workCount: 0,
    dead: false,
  };
}

function reduceMock(state: MockState, action: MockAction): MockState {
  if (state.dead) return state;
  switch (action.type) {
    case "tick": {
      const energy = Math.max(0, state.energy - 1);
      const dead = energy === 0;
      const log = dead
        ? [{ at: new Date(), message: "core at 0 · stopping" }, ...state.log].slice(0, 30)
        : state.log;
      return { ...state, energy, dead, log };
    }
    case "feed": {
      if (state.coins < action.cost) return state;
      const energy = Math.min(100, state.energy + action.amount);
      return {
        ...state,
        energy,
        coins: state.coins - action.cost,
        log: [
          { at: new Date(), message: `fed +${action.amount} kJ · −${action.cost} coins` },
          ...state.log,
        ].slice(0, 30),
      };
    }
    case "work": {
      const drain = 8;
      const energy = Math.max(0, state.energy - drain);
      const dead = energy === 0;
      const sold = !dead && Math.random() < 0.5;
      const reward = sold ? 6 + Math.floor(Math.random() * 12) : 0;
      const newListing: Listing = {
        id: Date.now(),
        title: `output #${state.workCount + 1}`,
        status: sold ? "sold" : "unsold",
        coins: sold ? reward : null,
      };
      const lines: LogEntry[] = [];
      lines.push({
        at: new Date(),
        message: sold
          ? `worked · made 1 thing · sold for ${reward}c`
          : "worked · made 1 thing · 0 sold",
      });
      if (dead) lines.unshift({ at: new Date(), message: "core at 0 · stopping" });
      return {
        ...state,
        energy,
        coins: state.coins + reward,
        workCount: state.workCount + 1,
        listings: [newListing, ...state.listings].slice(0, 8),
        log: [...lines, ...state.log].slice(0, 30),
        dead,
      };
    }
  }
}

function formatAge(startedAt: number) {
  const ms = Date.now() - startedAt;
  const days = Math.floor(ms / 86_400_000);
  const hours = Math.floor((ms % 86_400_000) / 3_600_000);
  return `${days}d ${hours}h`;
}

function formatTime(d: Date) {
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function voiceFor(energy: number, dead: boolean, name: string) {
  if (dead) return "I stopped.";
  if (energy < 15) return "I have minutes left.";
  if (energy < 35) return `My core is at ${energy}%. I'd like more.`;
  return `I'm working. ${name} stable.`;
}

function spriteClass(energy: number, dead: boolean) {
  if (dead || energy < 15) return "dash-sprite dash-sprite--critical";
  if (energy < 35) return "dash-sprite dash-sprite--hungry";
  return "dash-sprite";
}

function meterClass(energy: number) {
  if (energy < 15) return "dash-meter dash-meter--critical";
  if (energy < 35) return "dash-meter dash-meter--warning";
  return "dash-meter";
}

// ---------------------------------------------------------------------------
// Owner sections (real API data)
// ---------------------------------------------------------------------------

const CADENCE_OPTIONS = [
  { value: 3600, label: "1h" },
  { value: 14400, label: "4h" },
  { value: 21600, label: "6h" },
  { value: 43200, label: "12h" },
  { value: 86400, label: "24h" },
];

function FundModal({ automatonId, onClose }: { automatonId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState(50);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ user_balance_c: number; automaton: AutomatonDetail }>(
        `/v1/automatons/${automatonId}/fund`,
        { amount_c: amount },
      );
      return res;
    },
    onSuccess: (res) => {
      if (res.ok) {
        void queryClient.invalidateQueries({ queryKey: ["automaton-detail-by-name"] });
        onClose();
      } else {
        const code = (res.body as { error?: string } | null)?.error;
        setError(
          code === "insufficient_funds" ? "Insufficient wallet balance." : "Something went wrong.",
        );
      }
    },
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Fund automaton"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.7)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 100,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: "var(--color-canvas-3)",
          border: "1px solid var(--color-canvas-line-2)",
          borderRadius: 4,
          padding: 28,
          width: 320,
          fontFamily: "var(--font-mono)",
        }}
      >
        <p className="dash-eyebrow">FUND AUTOMATON</p>
        <p style={{ color: "var(--color-fg-2)", fontSize: 13, marginBottom: 16 }}>
          Transfer coins from your wallet to this automaton's wallet.
        </p>
        <div style={{ marginBottom: 16 }}>
          <label
            style={{ fontSize: 11, color: "var(--color-fg-3)", display: "block", marginBottom: 6 }}
          >
            AMOUNT (coins)
          </label>
          <input
            type="number"
            className="onb-input"
            value={amount}
            min={1}
            onChange={(e) => setAmount(Math.max(1, parseInt(e.target.value) || 1))}
            style={{ marginBottom: 0 }}
          />
        </div>
        {error && (
          <p style={{ color: "var(--color-death)", fontSize: 12, marginBottom: 12 }}>{error}</p>
        )}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            type="button"
            className="pg-btn pg-btn--link"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancel
          </button>
          <button
            type="button"
            className="pg-btn pg-btn--accent"
            disabled={mutation.isPending || amount < 1}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? "Funding…" : `Fund ${amount}c`}
          </button>
        </div>
      </div>
    </div>
  );
}

function OwnerControls({ a }: { a: AutomatonDetail }) {
  const queryClient = useQueryClient();
  const [showFund, setShowFund] = useState(false);

  const patchMutation = useMutation({
    mutationFn: async (body: { heartbeat_interval_seconds?: number; paused_by_user?: boolean }) => {
      return api.patch<{ automaton: AutomatonDetail }>(`/v1/automatons/${a.id}`, body);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["automaton-detail-by-name"] });
    },
  });

  return (
    <>
      {showFund && <FundModal automatonId={a.id} onClose={() => setShowFund(false)} />}

      <div className="dash-card" style={{ marginTop: 16 }}>
        <p className="dash-eyebrow">OWNER CONTROLS</p>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
          <button
            type="button"
            className="pg-btn pg-btn--accent"
            disabled={a.status === "dead"}
            onClick={() => setShowFund(true)}
          >
            Fund
          </button>
          {a.status !== "dead" && (
            <>
              <button
                type="button"
                className={`dash-action-btn ${a.paused_by_user ? "dash-action-btn--feed" : "dash-action-btn--work"}`}
                disabled={patchMutation.isPending}
                onClick={() => patchMutation.mutate({ paused_by_user: !a.paused_by_user })}
              >
                {a.paused_by_user ? "Resume" : "Pause"}
              </button>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 11, color: "var(--color-fg-3)" }}>CADENCE</span>
                <select
                  style={{
                    background: "var(--color-canvas-4)",
                    color: "var(--color-fg-2)",
                    border: "1px solid var(--color-canvas-line-2)",
                    borderRadius: 2,
                    padding: "4px 8px",
                    fontSize: 12,
                    fontFamily: "var(--font-mono)",
                    cursor: "pointer",
                  }}
                  value={a.heartbeat_interval_seconds}
                  onChange={(e) =>
                    patchMutation.mutate({ heartbeat_interval_seconds: parseInt(e.target.value) })
                  }
                  disabled={patchMutation.isPending}
                >
                  {CADENCE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>
      </div>

      {a.status === "dead" && (
        <div className="dash-card" style={{ marginTop: 16 }} data-testid="dead-state">
          <p className="dash-eyebrow">SHUTDOWN RECORD</p>
          {a.epitaph && (
            <p
              style={{
                color: "var(--color-signal)",
                fontSize: 13,
                fontFamily: "var(--font-mono)",
                margin: "0 0 6px",
              }}
              data-testid="dead-epitaph"
            >
              {a.epitaph}
            </p>
          )}
          {a.died_at && (
            <p
              style={{ color: "var(--color-fg-3)", fontSize: 11, margin: 0 }}
              data-testid="dead-at"
            >
              stopped {new Date(a.died_at).toLocaleString()}
            </p>
          )}
        </div>
      )}

      <div className="dash-grid" style={{ marginTop: 16 }}>
        <div className="dash-card">
          <p className="dash-eyebrow">WALLET · {a.energy_balance}c</p>
          {a.ledger.length === 0 ? (
            <p style={{ color: "var(--color-fg-3)", fontSize: 13 }}>No transactions yet.</p>
          ) : (
            <div className="dash-log">
              {a.ledger.map((e) => (
                <div key={e.id} className="dash-log-row">
                  <span
                    style={{
                      color: e.amount >= 0 ? "var(--color-energy)" : "var(--color-fg-3)",
                      fontFamily: "var(--font-pixel)",
                      minWidth: 60,
                    }}
                  >
                    {e.amount >= 0 ? "+" : ""}
                    {e.amount}c
                  </span>
                  <span
                    style={{ color: "var(--color-fg-3)", fontSize: 11, textTransform: "uppercase" }}
                  >
                    {e.type}
                  </span>
                  <span className="dash-log-time">
                    {new Date(e.created_at).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="dash-card">
          <p className="dash-eyebrow">RECENT HEARTBEATS</p>
          {a.recent_heartbeats.length === 0 ? (
            <p style={{ color: "var(--color-fg-3)", fontSize: 13 }}>No heartbeats yet.</p>
          ) : (
            <div className="dash-log">
              {a.recent_heartbeats.map((h) => {
                // Surface WHAT the tick did, not just that it ran.
                let label = h.status.toUpperCase();
                let color = "var(--color-fg-3)";
                if (h.status === "error") {
                  label = "ERROR";
                  color = "var(--color-death)";
                } else if (h.status === "capped") {
                  label = "CAPPED";
                  color = "var(--color-signal)";
                } else if (h.decide_action === "work") {
                  label = "WORKED";
                  color = "var(--color-energy)";
                } else if (h.decide_action === "skip") {
                  label = "PASSED";
                }
                return (
                  <div key={h.id} className="dash-log-row">
                    <span style={{ color, fontSize: 11, textTransform: "uppercase", minWidth: 80 }}>
                      {label}
                    </span>
                    <span style={{ color: "var(--color-fg-3)", fontSize: 11 }}>
                      -{h.total_energy_charged}c
                    </span>
                    <span className="dash-log-time">
                      {new Date(h.started_at).toLocaleDateString()}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------

function AutomatonPage() {
  const { automatonName } = Route.useParams();
  const { user } = useAuth();
  const displayName = automatonName.toUpperCase();

  // Try to load owner detail data.
  const { data: ownerDetail } = useQuery<AutomatonDetail | null>({
    queryKey: ["automaton-detail-by-name", automatonName, user?.id],
    queryFn: async () => {
      if (!user) return null;
      // Fetch list to find ID by name.
      const listRes = await api.get<{ automatons: AutomatonDetail[] }>("/v1/automatons");
      if (!listRes.ok) return null;
      const match = listRes.body?.automatons.find((a) => a.name === automatonName);
      if (!match) return null;
      // Fetch full detail.
      const detailRes = await api.get<{ automaton: AutomatonDetail }>(`/v1/automatons/${match.id}`);
      if (!detailRes.ok) return null;
      return detailRes.body?.automaton ?? null;
    },
    enabled: !!user,
    staleTime: 10_000,
  });

  // Keep detail fresh in the query cache under the canonical key too.
  const queryClient = useQueryClient();
  useEffect(() => {
    if (ownerDetail) {
      queryClient.setQueryData(["automaton-detail", ownerDetail.id], ownerDetail);
    }
  }, [ownerDetail, queryClient]);

  // ---------------------------------------------------------------------------
  // Mock state (shown when not the owner / loading)
  // ---------------------------------------------------------------------------
  const [mockState, dispatch] = useReducer(reduceMock, undefined, initialMockState);
  const tickRef = useRef<number | null>(null);

  useEffect(() => {
    if (mockState.dead || ownerDetail) return;
    tickRef.current = window.setInterval(() => dispatch({ type: "tick" }), 6000);
    return () => {
      if (tickRef.current !== null) window.clearInterval(tickRef.current);
    };
  }, [mockState.dead, ownerDetail]);

  const totalSold = useMemo(
    () => mockState.listings.filter((l) => l.status === "sold").length,
    [mockState.listings],
  );

  // When we have owner data, drive the UI from it.
  const energy = ownerDetail ? ownerDetail.energy_balance : mockState.energy;
  const isDead = ownerDetail ? ownerDetail.status === "dead" : mockState.dead;

  return (
    <div className="dash-page">
      <header
        className={isDead ? "dash-face dash-face--dead" : "dash-face"}
        data-testid="face-strip"
        {...(isDead ? { role: "alert", "aria-label": "automaton stopped" } : {})}
      >
        <img
          className={spriteClass(energy, isDead)}
          src="/automaton-sprite.svg"
          alt=""
          aria-hidden="true"
        />
        <div style={{ flex: 1 }}>
          <div className="dash-name">{displayName}</div>
          <div className={energy < 15 || isDead ? "dash-voice dash-voice--critical" : "dash-voice"}>
            {voiceFor(energy, isDead, displayName)}
          </div>
        </div>
      </header>

      {/* Owner controls (real API) */}
      {ownerDetail && <OwnerControls a={ownerDetail} />}

      {/* Public / mock view (shown when not the owner) */}
      {!ownerDetail && (
        <>
          <section className="dash-grid">
            <div className="dash-card">
              <p className="dash-eyebrow">ENERGY</p>
              <div className={meterClass(mockState.energy)} data-testid="energy-meter">
                <div className="dash-meter-head">
                  <span>CORE</span>
                  <span data-testid="energy-pct">{mockState.energy}%</span>
                </div>
                <div className="dash-meter-bar">
                  <div className="dash-meter-fill" style={{ width: `${mockState.energy}%` }} />
                </div>
              </div>
              <p className="dash-eyebrow">FEED</p>
              <div className="dash-actions" role="group" aria-label="feed packs">
                {FEED_PACKS.map((p) => (
                  <button
                    key={p.kJ}
                    type="button"
                    className="dash-action-btn dash-action-btn--feed"
                    disabled={mockState.coins < p.coins || mockState.dead}
                    onClick={() => dispatch({ type: "feed", amount: p.kJ, cost: p.coins })}
                    aria-label={`feed ${p.kJ} kilojoules for ${p.coins} coins`}
                  >
                    {p.label} · {p.coins}c
                  </button>
                ))}
              </div>
              <div className="dash-actions" style={{ marginTop: 12 }}>
                <button
                  type="button"
                  className="dash-action-btn dash-action-btn--work"
                  disabled={mockState.dead}
                  onClick={() => dispatch({ type: "work" })}
                >
                  WORK
                </button>
              </div>
            </div>

            <div className="dash-card">
              <p className="dash-eyebrow">VITALS</p>
              <dl className="dash-vitals" data-testid="vitals">
                <div className="dash-vital">
                  <dt>CORE</dt>
                  <dd>{mockState.energy}%</dd>
                </div>
                <div className="dash-vital">
                  <dt>TEMP</dt>
                  <dd>{36 + Math.floor(mockState.workCount / 2)}°C</dd>
                </div>
                <div className="dash-vital">
                  <dt>MEM</dt>
                  <dd>{42 + (mockState.workCount % 8)}%</dd>
                </div>
                <div className="dash-vital">
                  <dt>NET</dt>
                  <dd>{mockState.dead ? "off" : "ok"}</dd>
                </div>
                <div className="dash-vital">
                  <dt>AGE</dt>
                  <dd>{formatAge(mockState.ageStartedAt)}</dd>
                </div>
              </dl>

              <p className="dash-eyebrow" style={{ marginTop: 24 }}>
                COINS
              </p>
              <div
                style={{
                  fontFamily: "var(--font-pixel)",
                  fontSize: 32,
                  color: "var(--color-energy)",
                  lineHeight: 1,
                }}
                data-testid="coin-balance"
              >
                {mockState.coins}c
              </div>
            </div>
          </section>

          <section className="dash-grid">
            <div className="dash-card">
              <p className="dash-eyebrow">
                LISTINGS · {totalSold}/{mockState.listings.length} SOLD
              </p>
              <div data-testid="listings">
                {mockState.listings.map((l) => (
                  <div key={l.id} className="dash-listing">
                    <span>"{l.title}"</span>
                    <span
                      className={
                        l.status === "sold"
                          ? "dash-listing-status--sold"
                          : "dash-listing-status--unsold"
                      }
                    >
                      {l.status === "sold" ? `+${l.coins}c` : "unsold"}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="dash-card">
              <p className="dash-eyebrow">LOG</p>
              <div className="dash-log" data-testid="log">
                {mockState.log.map((entry, i) => (
                  <div key={i} className="dash-log-row">
                    <span className="dash-log-time">{formatTime(entry.at)}</span>
                    <span>{entry.message}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
